import {
  activityCommandsAreServerAuthoritative,
  completeActivityCommand,
  setActivityLiveScoreCommand,
} from '../lib/api/activities';
import { SaveRefused, saveWithRetry } from '../lib/saveWithRetry';

// ─────────────────────────────────────────────────────────────────────
// THE TWO ENKELTMATCH ACTIVITY WRITES, ONCE (#245, slice 4).
//
// AppShell and useBanepadPadActions each carried its own copy of «mark this
// activity completed» and «record its live score» — the same code twice, which
// is the drift the shared engine rules exist to prevent. They are one
// implementation here, and both hooks call it.
//
// Neither writes a document any more. On the http backend they send the
// REFERENCE — which match finished, what the score is — and the server decides
// the rest: completion stamps its own `completedAt`, because two courts must
// not disagree about when the same evening ended. Off that backend (memory,
// tests) the whole-list write is kept verbatim, so nothing changes there.
//
// FAILURE IS NOW VISIBLE (#276). `persistActivities` swallowed every write
// error, so a lost completion looked exactly like a saved one. These retry the
// blips invisibly and say so plainly when a save ultimately fails.
// ─────────────────────────────────────────────────────────────────────

export interface ActivityWriteDeps {
  activities: any[];
  persistActivities: (next: any[]) => Promise<void>;
  applyServerActivity: (updated: any) => void;
  showToast: (kind: string, message: string) => void;
}

const FAILED = 'Kunne ikke lagre. Sjekk nettet og prøv igjen.';

/** Runs a command, adopting the server's answer; reports a save that fails. */
async function commandOrReport(
  deps: ActivityWriteDeps,
  run: () => Promise<any>,
  opts: { onSlow?: () => void } = {},
): Promise<boolean> {
  try {
    const updated = await saveWithRetry(run, { onSlow: opts.onSlow });
    deps.applyServerActivity(updated);
    return true;
  } catch (e) {
    deps.showToast('error', e instanceof SaveRefused ? e.message : FAILED);
    return false;
  }
}

/**
 * Mark a single-match activity finished and link the match it produced.
 *
 * Returns whether the ACTIVITY was saved. The match itself is already
 * registered by the time this runs, so a failure here does not undo it — it is
 * reported, not turned into a failed result.
 */
export async function markSingleMatchActivityCompleted(
  deps: ActivityWriteDeps,
  activity: any,
  matchId: string | null,
): Promise<boolean> {
  if (activityCommandsAreServerAuthoritative()) {
    return commandOrReport(deps, () => completeActivityCommand(String(activity.id), matchId), {
      onSlow: () => deps.showToast('info', 'Lagrer resultatet …'),
    });
  }
  await deps.persistActivities(
    (deps.activities || []).map((a: any) =>
      a.id === activity.id
        ? {
            ...a,
            status: 'completed',
            completedAt: Date.now(),
            matches: [matchId].filter(Boolean),
          }
        : a,
    ),
  );
  return true;
}

/**
 * The running score for a single-match activity, written on every change.
 *
 * NOT finalised and no ELO — that is `markSingleMatchActivityCompleted`. This
 * is the highest-frequency activity write there is, which is exactly why it
 * stopped rewriting the club's whole list: a scoreboard updated courtside could
 * erase an activity staff created a moment earlier.
 */
export async function recordSingleMatchActivityLiveScore(
  deps: ActivityWriteDeps,
  activity: any,
  liveScore: any,
): Promise<boolean> {
  if (activityCommandsAreServerAuthoritative()) {
    // No «still saving» notice: on every point it would be noise rather than
    // information. A save that ultimately fails is still reported.
    return commandOrReport(deps, () => setActivityLiveScoreCommand(String(activity.id), liveScore));
  }
  await deps.persistActivities(
    (deps.activities || []).map((a: any) => (a.id === activity.id ? { ...a, liveScore } : a)),
  );
  return true;
}
